import { ApolloError } from '@apollo/client';
import { useCallback, useState } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

import { $downloadStreamedFile } from '../utils/fileUtils';
import { useGenerateDocumentDownloadTokenMutation } from './graphql/UseFileDownload.generated';

export function useDocumentDownload(): [
  (documentId: string) => Promise<void>,
  { loading: boolean; error: ApolloError | undefined },
] {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const [documentDownloading, setDocumentDownloading] = useState(false);
  const trackUserEvent = useTrackUserEvent();
  const [
    generateDownloadTokenv2,
    { loading: generatingDownloadTokenv2, error: errorv2 },
  ] = useGenerateDocumentDownloadTokenMutation();

  const downloadFile = useCallback(
    async (documentId: string) => {
      try {
        setDocumentDownloading(true);
        const result = await generateDownloadTokenv2({
          variables: {
            documentID: documentId,
          },
        });

        const tokenDetails = result.data;
        if (!tokenDetails) {
          // this shouldn't happen
          showFeedback('There was an error downloading the document.');
          reportError('invalid token details');
          return;
        }

        // /api/download/document?token=<token>
        await $downloadStreamedFile(
          `/api/download/document?token=${tokenDetails.requestDocumentDownload.token}`
        );
        showFeedback(`Document downloaded successfully.`, {
          variant: 'success',
        });

        trackUserEvent('document downloaded', {
          documentId: documentId,
        });
      } catch (err) {
        reportError('failed to download document from s3', err as Error);
        showFeedback(
          'There was an error downloading the document. Please try again.'
        );
        throw err; // Re-throw to maintain promise rejection
      } finally {
        setDocumentDownloading(false);
      }
    },
    [generateDownloadTokenv2, reportError, showFeedback, trackUserEvent]
  );

  const loading = generatingDownloadTokenv2 || documentDownloading;
  return [downloadFile, { loading, error: errorv2 }];
}
